import { Participant } from '@app/api';
import moment from 'moment-timezone';
import { Field, FieldAnswer } from '../queries';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type FlatFormAnswer = string | number | Date | undefined | string[] | boolean;

export const useAllOtherTranslations = () => {
  const { i18n } = useTranslation();
  const data = i18n.store.data;

  return useMemo(() => {
    const result = new Set<string>();

    Object.keys(data).forEach((lng) => {
      const value = i18n.getResource(lng, 'client', 'screeningForm.other');
      if (typeof value === 'string') {
        result.add(value.trim().toLowerCase());
      }
    });

    return result;
  }, [data, i18n]);
};

export interface FlatFormAnswers {
  [key: string]: FlatFormAnswer;
}

export const participantToFlatFormAnwsers = (
  participant?: Partial<Participant>,
): FlatFormAnswers => {
  if (!participant) {
    return {};
  }
  const { appointments, tags, ...flatAnswers } = participant;
  return flatAnswers as FlatFormAnswers;
};

export const getAnswerFlatValue = (answer?: FieldAnswer): FlatFormAnswer => {
  if (!answer || !answer.type) {
    return '';
  }

  switch (answer.type) {
    case 'choice':
      if (!answer.choice) {
        return undefined;
      }
      return answer.choice && 'ref' in answer.choice
        ? answer.choice.ref
        : undefined;
    case 'choices':
      if (!answer.choices) {
        return undefined;
      }
      let choices: string[] = [];
      if ('refs' in answer.choices && answer.choices.refs) {
        choices = answer.choices.refs;
      }
      return choices;
    case 'date':
      return moment(answer.date).format();

    default:
      const res = `${answer[answer.type as keyof typeof answer] || ''}`;
      return res ? res : undefined;
  }
};

export const getFieldInitalValue = (
  field: Field,
  currentValues?: FlatFormAnswers,
): FlatFormAnswer | undefined => {
  // @deprecated
  if (field.ref === 'ageRange' && currentValues) {
    const value = currentValues.birthdate;
    if (!(value instanceof Date) && typeof value !== 'string') {
      return value;
    }
    const date = moment(value);

    if (
      !('properties' in field) ||
      !('choices' in field.properties) ||
      !field.properties.choices
    ) {
      return value;
    }

    const foundOption = field.properties.choices.find((choice) => {
      const { ref } = choice;
      const res = /ageRange-([0-9]+)-([0-9]+)/.exec(ref);
      if (!res) {
        return false;
      }
      const minDate = moment().subtract(Number(res[2]) + 1, 'years');
      const maxDate = moment().subtract(Number(res[1]), 'years');
      return date.isBetween(minDate, maxDate);
    });
    if (!foundOption) {
      return value;
    }
    return foundOption.ref;
  }

  return currentValues && currentValues[field.ref];
};
